<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import KanbanReportWidget from 'kanban/components/reports/KanbanReportWidget.vue';
import {
  formatNumber,
  formatPercent,
  formatCurrencyValue,
} from 'kanban/helper/reportFormatters';

const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  locale: {
    type: String,
    default: 'en',
  },
});

const emit = defineEmits(['retry']);

const { t } = useI18n();

const agents = computed(() => props.data?.agents || []);

const agentName = agent =>
  agent.name || t('KANBAN.REPORTS.LEADERBOARD.UNKNOWN_AGENT');

const winRateLabel = agent =>
  agent.win_rate === null || agent.win_rate === undefined
    ? t('KANBAN.REPORTS.SUMMARY.WIN_RATE_NA')
    : formatPercent(agent.win_rate, props.locale);

const revenueLabel = agent => {
  const list = agent.revenue_by_currency || [];
  if (!list.length) return t('KANBAN.REPORTS.NONE');
  return list
    .map(entry =>
      formatCurrencyValue(entry.revenue, entry.currency, props.locale, {
        compact: true,
      })
    )
    .join(' · ');
};
</script>

<template>
  <KanbanReportWidget
    :title="t('KANBAN.REPORTS.LEADERBOARD.TITLE')"
    :info-text="t('KANBAN.REPORTS.LEADERBOARD.INFO')"
    :is-loading="isLoading"
    :error="error"
    :is-empty="!agents.length"
    :empty-text="t('KANBAN.REPORTS.LEADERBOARD.EMPTY')"
    @retry="emit('retry')"
  >
    <div class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="text-left text-xs font-medium text-n-slate-10">
            <th class="py-2 pr-4 font-medium">
              {{ t('KANBAN.REPORTS.LEADERBOARD.AGENT') }}
            </th>
            <th class="py-2 px-4 font-medium text-right">
              {{ t('KANBAN.REPORTS.LEADERBOARD.WON') }}
            </th>
            <th class="py-2 px-4 font-medium text-right">
              {{ t('KANBAN.REPORTS.LEADERBOARD.LOST') }}
            </th>
            <th class="py-2 px-4 font-medium text-right">
              {{ t('KANBAN.REPORTS.LEADERBOARD.WIN_RATE') }}
            </th>
            <th class="py-2 pl-4 font-medium text-right">
              {{ t('KANBAN.REPORTS.LEADERBOARD.REVENUE') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="agent in agents"
            :key="agent.agent_id"
            class="border-t border-n-slate-3"
          >
            <td class="py-2 pr-4">
              <div class="flex items-center gap-2 min-w-0">
                <Avatar
                  :src="agent.avatar_url"
                  :name="agentName(agent)"
                  :size="24"
                />
                <span class="truncate text-n-slate-12">
                  {{ agentName(agent) }}
                </span>
              </div>
            </td>
            <td class="py-2 px-4 text-right tabular-nums text-n-teal-11">
              {{ formatNumber(agent.deals_won, locale) }}
            </td>
            <td class="py-2 px-4 text-right tabular-nums text-n-ruby-11">
              {{ formatNumber(agent.deals_lost, locale) }}
            </td>
            <td class="py-2 px-4 text-right tabular-nums text-n-slate-12">
              {{ winRateLabel(agent) }}
            </td>
            <td class="py-2 pl-4 text-right tabular-nums text-n-slate-12">
              {{ revenueLabel(agent) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </KanbanReportWidget>
</template>
